// Authentication service for the host app
/* eslint-disable no-console */
type UpdateUserTokenCallback = (token: string) => void;

type AuthToken = {
  auth_token: string;
};

export type SecurityPermissions = {
  row_level_permissions?: Array<{
    logical_groups: {
      logical_group: Array<{
        id: string;
        restricted: string;
        matter_ids: string[];
      }>;
    };
  }>;

  user_access?: {
    logical_operators: string;
    roles: string[];
    departments: string[];
  };
};

export type UserSessionRequest = {
  data: {
    type: 'users';
    attributes: {
      first_name: string;
      last_name: string;
      email_address: string;
      source_account_id: string;
      source_user_id: string;
      timezone_hours_offset: number;
      security_permissions: SecurityPermissions;
    };
  };
};

type UserSessionResponse = {
  data: {
    attributes: {
      user: {
        ask_aries_consent: string;
        ask_aries_consent_provided_at: string;
      };
      token: string;
    };
  };
};

type SessionData = {
  askAriesConsent: string;
  askAriesConsentProvidedAt: string;
  token: string;
  userToken: string;
};

let tokenRefreshInterval: NodeJS.Timeout | null = null;
let updateUserTokenCallback: UpdateUserTokenCallback | null = null;

export function setUpdateUserTokenCallback(callback: UpdateUserTokenCallback): void {
  updateUserTokenCallback = callback;
}

/**
 * Checks if the Auth0 token is valid by decoding it and checking expiration
 * @returns boolean indicating if token is valid
 */
function isAuth0TokenValid(): boolean {
  const auth0Token = localStorage.getItem('auth0Token');

  if (!auth0Token) {
    return false;
  }

  try {
    // JWT tokens have 3 parts separated by dots
    const tokenParts = auth0Token.split('.');
    if (tokenParts.length !== 3) {
      return false;
    }

    // Decode the payload (middle part)
    const payload = JSON.parse(atob(tokenParts[1]));

    // Check if token has expired
    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < currentTime) {
      // Token has expired
      console.log('Auth0 token has expired');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating Auth0 token:', error);
    return false;
  }
}

export async function getAuthToken(productKey?: string): Promise<string> {
  // Use the provided productKey or fall back to the default one
  const key = productKey || import.meta.env.VITE_STAGING_DATA_HUB_PRODUCT_KEY;

  const authEndpoint = `${import.meta.env.VITE_STAGING_DATA_HUB_API_URL}/authenticate`;

  // Get and validate the Auth0 token
  const auth0Token = isAuth0TokenValid() ? localStorage.getItem('auth0Token') : null;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-datahub-apikey': key,
  };

  // Add Auth0 token to headers if available and valid
  if (auth0Token) {
    headers['Authorization'] = `Bearer ${auth0Token}`;
  }

  const response = await fetch(authEndpoint, {
    method: 'POST',
    headers,
  });
  const data = (await response.json()) as AuthToken;
  return data.auth_token;
}

export async function createUserSession(
  authToken: string,
  requestBody?: UserSessionRequest
): Promise<SessionData> {
  const createUserSessionEndpoint = `${import.meta.env.VITE_STAGING_DATA_HUB_API_URL}/ask_aries/create_user_session`;

  // // If no request body is provided, use the default user (John Staging)
  // if (!requestBody) {
  //   if (!localStorage.getItem('stagingUserId')) {
  //     localStorage.setItem('stagingUserId', getRandomUuidv4());
  //   }

  //   const stagingUserId = localStorage.getItem('stagingUserId') || getRandomUuidv4();

  //   requestBody = {
  //     data: {
  //       type: 'users',
  //       attributes: {
  //         first_name: 'John',
  //         last_name: 'Staging',
  //         email_address: 'test.test@mitratech.com',
  //         source_account_id: 'staging-source-account-id',
  //         source_user_id: stagingUserId,
  //         timezone_hours_offset: 12,
  //         security_permissions: {
  //           row_level_permissions: [
  //             {
  //               logical_groups: {
  //                 logical_group: [
  //                   {
  //                     id: 'abc',
  //                     restricted: 'false',
  //                     matter_ids: ['1'],
  //                   },
  //                 ],
  //               },
  //             },
  //           ],
  //         },
  //       },
  //     },
  //   };
  // }

  const response = await fetch(createUserSessionEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  const rawData = (await response.json()) as UserSessionResponse;

  return {
    askAriesConsent: rawData.data.attributes.user.ask_aries_consent,
    askAriesConsentProvidedAt: rawData.data.attributes.user.ask_aries_consent_provided_at,
    token: authToken,
    userToken: rawData.data.attributes.token,
  };
}

export async function refreshUserToken(
  currentAuthToken: string,
  requestBody?: UserSessionRequest,
  productKey?: string
): Promise<string> {
  try {
    const sessionData = await createUserSession(currentAuthToken, requestBody);
    return sessionData.userToken;
  } catch (error) {
    console.error('Failed to refresh user token:', error);
    try {
      const newAuthToken = await getAuthToken(productKey);
      const sessionData = await createUserSession(newAuthToken, requestBody);
      return sessionData.userToken;
    } catch (refreshError) {
      console.error('Failed to refresh token with new auth token:', refreshError);
      throw refreshError;
    }
  }
}

export async function handleAuthentication(
  requestBody?: UserSessionRequest,
  productKey?: string
): Promise<SessionData> {
  try {
    const authToken = await getAuthToken(productKey);
    const sessionData = await createUserSession(authToken, requestBody);

    const REFRESH_INTERVAL = 2 * 60 * 1000;

    cleanup();

    tokenRefreshInterval = setInterval(async () => {
      try {
        if (import.meta.env.VITE_MICRO_FRONTEND_LOADER_VERSION === 'v2') {
          console.log('Token refresh not needed in v2 version');
          return;
        }
        console.log('Refreshing user token...');
        const newUserToken = await refreshUserToken(authToken, requestBody, productKey);
        if (updateUserTokenCallback) {
          updateUserTokenCallback(newUserToken);
          console.log('User token refreshed successfully');
        } else {
          console.error('updateUserToken callback not set');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }, REFRESH_INTERVAL);

    return sessionData;
  } catch (error) {
    console.error('DataHub authentication flow failed:', error);
    throw error;
  }
}

export function cleanup(): void {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
}
